import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { DnsRecord, DnsRicerca, DnsZone, DnsZones } from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner, timeAgo } from '../components/common'
import { DataTable } from '../components/DataTable'
import { EditorRecord, TogliRecord } from '../components/dns/EditorRecord'
import {
  TIPI_SCRIVIBILI, chiaveZona, diSistema, formatTtl, nomeRelativo, registrazioneVecchia, reteZonaInversa,
} from '../components/dns/dnsUtil'
import { NotaCache } from '../components/NotaCache'
import { ScritturaSpenta, useScrittura } from '../components/ScritturaModulo'
import { useAsync, useVivo } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * The DNS page: the customer's DNS servers (PowerDNS, Microsoft DNS), read
 * through the probe.
 *
 * - **Opens at once**: zones and records come from the copy of the last read
 *   (`live_cache`), with the date; «Aggiorna» reads again in the background and
 *   the page refreshes by itself. A periodic pass keeps the copies fresh, so the
 *   search across all zones answers instantly.
 * - **Two panes**: the zones on the left (forward and reverse, filterable), the
 *   records of the chosen one on the right — filters by type with the counts, a
 *   text filter, the zone's own records (SOA, NS) hidden, and for Microsoft DNS
 *   the dynamic records split from the static ones, with those not refreshed for
 *   30 days marked (a client that no longer registers). The server and the zone
 *   live in the address, so a zone can be linked and «back» works.
 * - **Addresses lead to the CMDB**: an A/AAAA whose address belongs to a CI
 *   links to it.
 * - **Writing is guided** (when the connection allows it): `EditorRecord` asks
 *   the server first and offers the choices — replace or add next to, create or
 *   update the PTR, remove the old one — and `TogliRecord` can take the PTR
 *   along.
 */
export default function Dns() {
  const [params, setParams] = useSearchParams()
  const server = useAsync(() => api.dnsServers(), [])
  const idServer = params.get('server') ?? server.data?.[0]?.id ?? null
  const zonaId = params.get('zone')
  const zone = useVivo((refresh) => api.dnsZones(idServer, refresh), [idServer])
  const dettaglio = useVivo(
    (refresh) => (zonaId ? api.dnsZoneRecords(zonaId, idServer, refresh) : Promise.resolve(null as never)),
    [zonaId, idServer],
  )
  const scrittura = useScrittura('dns', server.data?.find((x) => x.id === idServer)?.write_enabled)

  const [filtroTesto, setFiltroTesto] = useState('')
  const [fatto, setFatto] = useState('')
  const [editor, setEditor] = useState<{ record?: DnsRecord; tipo?: string } | null>(null)
  const [daTogliere, setDaTogliere] = useState<DnsRecord | null>(null)

  function scegli(cambi: Record<string, string | null>) {
    const nuovi = new URLSearchParams(params)
    for (const [k, v] of Object.entries(cambi)) {
      if (v) nuovi.set(k, v)
      else nuovi.delete(k)
    }
    setParams(nuovi)
  }

  const pannello = useRef<HTMLElement>(null)
  function apriZona(id: string, filtro = '') {
    setFatto('')
    setFiltroTesto(filtro)
    scegli({ zone: id })
    // The zone list scrolls by itself, but the page may be scrolled down
    // through a long zone: if the records start out of view (or under the
    // fixed top bar), bring their top in, just below the bar.
    const el = pannello.current
    if (!el) return
    const barra = document.querySelector('.topbar')?.getBoundingClientRect().height ?? 56
    const sopra = el.getBoundingClientRect().top - barra - 12
    if (sopra < 0 || sopra > window.innerHeight * 0.6) window.scrollBy({ top: sopra, behavior: 'smooth' })
  }

  async function dopoScrittura(messaggio: string) {
    setEditor(null)
    setDaTogliere(null)
    setFatto(messaggio)
    // The Control Plane has already patched the copy and reads the zone again
    // in the background: ask for the copy, without starting another read.
    await dettaglio.rileggiCopia()
  }

  const z = zone.data
  const zoneTutte = z?.available ? z.zones ?? [] : []
  const zonaScelta = zoneTutte.find((x) => x.id === zonaId)
  const nomeZona = dettaglio.data?.zone?.name ?? zonaScelta?.name ?? zonaId ?? ''

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">DNS</h1>
          <p className="page__subtitle">
            {t('Inventario delle zone DNS e dei relativi record acquisiti dai server PowerDNS e Microsoft DNS. I dati sono riferiti all’ultima sincronizzazione disponibile e si aggiornano automaticamente in sottofondo.')}
          </p>
        </div>
        {(server.data?.length ?? 0) > 1 && (
          <div className="field" style={{ minWidth: 260, marginBottom: 0 }}>
            <label htmlFor="dns-server">{t('Server DNS')}</label>
            <select id="dns-server" value={idServer ?? ''} onChange={(e) => { setFatto(''); scegli({ server: e.target.value, zone: null }) }}>
              {(server.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>{s.host ?? s.name} · {s.provider}</option>
              ))}
            </select>
          </div>
        )}
      </header>

      <ErrorBanner error={server.error ?? zone.error} />

      {z?.available && <RicercaGlobale server={idServer} onApri={apriZona} />}

      <div className="dns-layout">
        <ElencoZone
          dati={z}
          loading={zone.loading}
          scelta={zonaId}
          onScegli={(id) => apriZona(id)}
          onAggiorna={() => void zone.reload()}
          microsoft={z?.server?.kind === 'msdns'}
        />

        <section className="dns-pannello" ref={pannello}>
          {!zonaId ? (
            <Card>
              <EmptyState
                title={t('Scegli una zona')}
                hint={t('Seleziona una zona DNS dall’elenco a sinistra: le zone dirette sono in alto, le zone inverse (PTR) in basso. In alternativa, usa la ricerca per trovare un nome DNS o un indirizzo IP in tutte le zone configurate.')}
              />
            </Card>
          ) : (
            <Card
              title={reteZonaInversa(nomeZona) ? `${reteZonaInversa(nomeZona)} · ${nomeZona}` : nomeZona}
              actions={
                <>
                  {scrittura.puoScrivere && dettaglio.data?.available && (
                    <button onClick={() => { setFatto(''); setEditor({}) }}>+ {t('Nuovo record')}</button>
                  )}{' '}
                  <button className="secondary" onClick={() => void dettaglio.reload()} disabled={dettaglio.loading}>
                    {dettaglio.loading ? t('Aggiorno…') : t('Aggiorna')}
                  </button>
                </>
              }
            >
              <SegniZona zona={zonaScelta} serial={dettaglio.data?.zone?.serial ?? null} />
              <NotaCache dati={dettaglio.data} />
              {fatto && <div className="banner banner--ok">{fatto}</div>}
              <ErrorBanner error={dettaglio.error} />
              {scrittura.spentaQui && dettaglio.data?.available && <ScritturaSpenta />}
              {dettaglio.data?.truncated && (
                <div className="banner banner--warn">
                  {t('La zona è troppo grande per una lettura sola: gli ultimi record sono stati tagliati. Quelli mostrati sono corretti, ma non sono tutti.')}
                </div>
              )}
              {dettaglio.loading && !dettaglio.data ? (
                <Spinner label={t('Interrogo il DNS…')} />
              ) : !dettaglio.data ? null : !dettaglio.data.available ? (
                <p className="muted">{dettaglio.data.reason ?? t('DNS non interrogabile.')}</p>
              ) : (
                <RecordZona
                  zona={nomeZona}
                  records={dettaglio.data.records ?? []}
                  microsoft={dettaglio.data.server?.kind === 'msdns'}
                  filtroTesto={filtroTesto}
                  onFiltroTesto={setFiltroTesto}
                  puoScrivere={scrittura.puoScrivere}
                  onModifica={(r) => { setFatto(''); setEditor({ record: r }) }}
                  onTogli={(r) => { setFatto(''); setDaTogliere(r) }}
                  onApriNome={(nome) => setFiltroTesto(nome)}
                />
              )}
            </Card>
          )}
        </section>
      </div>

      {editor && zonaId && (
        <EditorRecord
          zona={nomeZona}
          zoneId={zonaId}
          server={idServer}
          record={editor.record}
          tipoIniziale={editor.tipo}
          onChiudi={() => setEditor(null)}
          onFatto={(m) => void dopoScrittura(m)}
        />
      )}
      {daTogliere && zonaId && (
        <TogliRecord
          zona={nomeZona}
          zoneId={zonaId}
          server={idServer}
          record={daTogliere}
          onChiudi={() => setDaTogliere(null)}
          onFatto={(m) => void dopoScrittura(m)}
        />
      )}

    </>
  )
}

/** The zone list: forward and reverse, filterable, with what matters of each zone. */
function ElencoZone({ dati, loading, scelta, onScegli, onAggiorna, microsoft }: {
  dati: DnsZones | null
  loading: boolean
  scelta: string | null
  onScegli: (id: string) => void
  onAggiorna: () => void
  microsoft: boolean
}) {
  const [filtro, setFiltro] = useState('')
  const zone = dati?.available ? dati.zones ?? [] : []
  const f = filtro.trim().toLowerCase()
  const inversa = (x: DnsZone) => x.reverse ?? (x.name ?? '').toLowerCase().endsWith('.arpa')
  const visibili = zone
    .filter((x) => !f || (x.name ?? x.id).toLowerCase().includes(f) || (reteZonaInversa(x.name ?? x.id) ?? '').includes(f))
    .sort((a, b) => chiaveZona(a.name ?? a.id).localeCompare(chiaveZona(b.name ?? b.id)))
  const dirette = visibili.filter((x) => !inversa(x))
  const inverse = visibili.filter(inversa)
  // The reverse zones are many (one per subnet) and opened less often: closed
  // by default when they are more than a handful, open if the chosen zone is one.
  const sceltaInversa = zone.some((x) => x.id === scelta && inversa(x))
  const [aperte, setAperte] = useState({ dirette: true, inverse: false })
  const inverseAperte = aperte.inverse || sceltaInversa || zone.filter(inversa).length <= 10

  return (
    <aside className="dns-zone">
      <Card
        title={zone.length ? t('Zone ({n})', { n: zone.length }) : t('Zone')}
        actions={
          <button className="secondary" onClick={onAggiorna} disabled={loading}>
            {loading ? t('Aggiorno…') : t('Aggiorna')}
          </button>
        }
      >
        <NotaCache dati={dati} />
        {loading && !dati ? (
          <Spinner label={t('Interrogo il DNS…')} />
        ) : !dati ? null : !dati.available ? (
          <>
            <p className="muted">{dati.reason ?? t('DNS non interrogabile.')}</p>
            <p className="muted"><Link to="/integrations">{t('Le connessioni DNS si configurano in Sorgenti dati.')}</Link></p>
          </>
        ) : !zone.length ? (
          <EmptyState
            title={t('Nessuna zona')}
            hint={microsoft
              ? t('Il server non ha zone, oppure l’utente non ha i permessi di lettura sul DNS.')
              : t('Il DNS non ha zone, oppure la chiave API non ha i permessi.')}
          />
        ) : (
          <>
            {zone.length > 8 && (
              <input
                type="text"
                className="dns-zone__filtro"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                placeholder={t('Filtra per nome o rete…')}
                aria-label={t('Filtra le zone')}
              />
            )}
            <div className="dns-zone__scorri">
              <GruppoZone titolo={t('Dirette')} zone={dirette} scelta={scelta} onScegli={onScegli}
                aperto={aperte.dirette || Boolean(f)} onApri={() => setAperte((a) => ({ ...a, dirette: !a.dirette }))} />
              <GruppoZone titolo={t('Inverse')} zone={inverse} scelta={scelta} onScegli={onScegli}
                aperto={inverseAperte || Boolean(f)} onApri={() => setAperte((a) => ({ ...a, inverse: !a.inverse }))} />
            </div>
          </>
        )}
      </Card>
    </aside>
  )
}

function GruppoZone({ titolo, zone, scelta, onScegli, aperto, onApri }: {
  titolo: string
  zone: DnsZone[]
  scelta: string | null
  onScegli: (id: string) => void
  aperto: boolean
  onApri: () => void
}) {
  // Paged, so a server with dozens of reverse zones stays a short list. It
  // opens on the page of the chosen zone; a new filter goes back to the first.
  const pagine = Math.max(1, Math.ceil(zone.length / ZONE_PER_PAGINA))
  const indiceScelta = zone.findIndex((x) => x.id === scelta)
  const [pagina, setPagina] = useState(indiceScelta >= 0 ? Math.floor(indiceScelta / ZONE_PER_PAGINA) : 0)
  const firma = zone.map((x) => x.id).join('|')
  useEffect(() => {
    setPagina(indiceScelta >= 0 ? Math.floor(indiceScelta / ZONE_PER_PAGINA) : 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firma])
  const corrente = Math.min(pagina, pagine - 1)
  const visibili = zone.slice(corrente * ZONE_PER_PAGINA, (corrente + 1) * ZONE_PER_PAGINA)
  if (!zone.length) return null
  return (
    <>
      <button className="dns-gruppo" onClick={onApri} aria-expanded={aperto}>
        <span>{aperto ? '▾' : '▸'} {titolo}</span>
        <span>{zone.length}</span>
      </button>
      {aperto && (
        <>
        <ul className="dns-zone__elenco">
          {visibili.map((zn) => {
            const nome = zn.name ?? zn.id
            const rete = reteZonaInversa(nome)
            return (
              <li key={zn.id}>
                <button
                  className={`dns-zone__voce${scelta === zn.id ? ' dns-zone__voce--scelta' : ''}`}
                  onClick={() => onScegli(zn.id)}
                  aria-current={scelta === zn.id ? 'true' : undefined}
                  title={rete ? `${rete} · ${nome}` : nome}
                >
                  <span className="dns-zone__nome">{rete ?? nome}</span>
                  <span className="dns-zone__segni">
                    {zn.paused && <span className="badge badge--warn">{t('in pausa')}</span>}
                    {zn.dnssec && <span className="badge badge--ok">DNSSEC</span>}
                    {zn.kind && !['Primary', 'Master', 'Native'].includes(zn.kind) && <span className="badge badge--muted">{zn.kind}</span>}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
        {pagine > 1 && (
          <div className="dns-zone__pagine">
            <button className="secondary" onClick={() => setPagina(corrente - 1)} disabled={corrente === 0} aria-label={t('Pagina precedente')}>‹</button>
            <span>{t('{da}–{a} di {n}', { da: corrente * ZONE_PER_PAGINA + 1, a: Math.min(zone.length, (corrente + 1) * ZONE_PER_PAGINA), n: zone.length })}</span>
            <button className="secondary" onClick={() => setPagina(corrente + 1)} disabled={corrente >= pagine - 1} aria-label={t('Pagina successiva')}>›</button>
          </div>
        )}
        </>
      )}
    </>
  )
}

/** How many zones a group shows per page. */
const ZONE_PER_PAGINA = 20

/** What a zone is, in one line: type, AD replication, dynamic update, serial. */
function SegniZona({ zona, serial }: { zona?: DnsZone; serial: number | null }) {
  if (!zona && serial == null) return null
  const parti: string[] = []
  if (zona?.kind) parti.push(zona.kind)
  if (zona?.ad_integrated) parti.push(t('integrata in AD ({replica})', { replica: zona.replication ?? 'AD' }))
  if (zona?.dynamic_update) parti.push(t('aggiornamento dinamico: {modo}', { modo: zona.dynamic_update }))
  if (serial != null) parti.push(t('serial {n}', { n: serial }))
  return parti.length ? <p className="muted dns-segni">{parti.join(' · ')}</p> : null
}

type Registrazione = 'tutti' | 'dinamici' | 'statici' | 'vecchi'

/** The records of a zone: type chips with counts, a text filter, the zone's own records hidden. */
function RecordZona({ zona, records, microsoft, filtroTesto, onFiltroTesto, puoScrivere, onModifica, onTogli, onApriNome }: {
  zona: string
  records: DnsRecord[]
  microsoft: boolean
  filtroTesto: string
  onFiltroTesto: (v: string) => void
  puoScrivere: boolean
  onModifica: (r: DnsRecord) => void
  onTogli: (r: DnsRecord) => void
  onApriNome: (nome: string) => void
}) {
  const [tipo, setTipo] = useState<string | null>(null)
  const [sistema, setSistema] = useState(false)
  const [registrazione, setRegistrazione] = useState<Registrazione>('tutti')

  const base = useMemo(() => records.filter((r) => sistema || !diSistema(r, zona)), [records, sistema, zona])
  const conteggi = useMemo(() => {
    const c = new Map<string, number>()
    for (const r of base) c.set(r.type ?? '?', (c.get(r.type ?? '?') ?? 0) + 1)
    return [...c.entries()].sort((a, b) => b[1] - a[1])
  }, [base])
  const vecchi = useMemo(() => base.filter((r) => registrazioneVecchia(r.timestamp)).length, [base])
  const f = filtroTesto.trim().toLowerCase()
  const righe = base.filter((r) =>
    (!tipo || r.type === tipo)
    && (!f || (r.name ?? '').toLowerCase().includes(f) || (r.content ?? '').toLowerCase().includes(f) || (r.ci?.name ?? '').toLowerCase().includes(f))
    && (registrazione === 'tutti'
      || (registrazione === 'dinamici' && Boolean(r.timestamp))
      || (registrazione === 'statici' && !r.timestamp)
      || (registrazione === 'vecchi' && registrazioneVecchia(r.timestamp))))
  const nomiInZona = useMemo(() => new Set(records.map((r) => (r.name ?? '').toLowerCase())), [records])

  const colonne = [
    {
      key: 'name', header: t('Nome'),
      value: (r: DnsRecord) => nomeRelativo(r.name, zona),
      render: (r: DnsRecord) => <span className="mono dns-nome-record" title={r.name ?? ''}>{nomeRelativo(r.name, zona)}</span>,
    },
    {
      key: 'type', header: t('Tipo'),
      value: (r: DnsRecord) => r.type ?? '',
      render: (r: DnsRecord) => <span className={`dns-tipo dns-tipo--${(r.type ?? 'x').toLowerCase()}`}>{r.type}</span>,
    },
    {
      key: 'content', header: t('Valore'),
      value: (r: DnsRecord) => r.content ?? '',
      render: (r: DnsRecord) => {
        const bersaglio = (r.content ?? '').trim().split(/\s+/).pop()?.replace(/\.$/, '').toLowerCase() ?? ''
        return (
          <span className="dns-valore">
            <span className="mono">{r.content}</span>
            {r.ci && <> <Link to={`/cis/${r.ci.id}`} className="dns-ci" title={t('Il CI della CMDB con questo indirizzo')}>→ {r.ci.name}</Link></>}
            {['CNAME', 'MX', 'SRV', 'NS'].includes(r.type ?? '') && nomiInZona.has(bersaglio) && (
              <> <button className="link" onClick={() => onApriNome(bersaglio)} title={t('Mostra i record del nome a cui punta')}>{t('vai')}</button></>
            )}
          </span>
        )
      },
    },
    {
      key: 'ttl', header: 'TTL',
      value: (r: DnsRecord) => r.ttl ?? 0,
      render: (r: DnsRecord) => <span title={r.ttl == null ? '' : t('{n} secondi', { n: r.ttl })}>{formatTtl(r.ttl)}</span>,
    },
    ...(microsoft
      ? [{
          key: 'timestamp', header: t('Registrato'),
          value: (r: DnsRecord) => r.timestamp ?? '',
          render: (r: DnsRecord) =>
            r.timestamp ? (
              <span title={new Date(r.timestamp).toLocaleString(localeCorrente())}>
                {timeAgo(r.timestamp)}
                {registrazioneVecchia(r.timestamp) && <> <span className="badge badge--warn">{t('vecchio')}</span></>}
              </span>
            ) : <span className="muted">{t('statico')}</span>,
        }]
      : [{
          key: 'disabled', header: t('Stato'),
          value: (r: DnsRecord) => (r.disabled ? t('disabilitato') : t('attivo')),
          render: (r: DnsRecord) => r.disabled
            ? <span className="badge badge--warn">{t('disabilitato')}</span>
            : <span className="badge badge--ok">{t('attivo')}</span>,
        }]),
    ...(puoScrivere
      ? [{
          key: 'azioni', header: '',
          value: () => '',
          render: (r: DnsRecord) =>
            TIPI_SCRIVIBILI.includes(r.type ?? '') ? (
              <span style={{ whiteSpace: 'nowrap' }}>
                <button className="link" onClick={() => onModifica(r)}>{t('Modifica')}</button>{' '}
                <button className="link" onClick={() => onTogli(r)}>{t('Togli')}</button>
              </span>
            ) : null,
        }]
      : []),
  ]

  if (!records.length) return <EmptyState title={t('Nessun record')} hint="" />
  return (
    <>
      <div className="dns-chips" role="group" aria-label={t('Tipo')}>
        <button className={`dns-chip${tipo == null ? ' dns-chip--attivo' : ''}`} onClick={() => setTipo(null)}>
          {t('Tutti')} <span className="dns-chip__n">{base.length}</span>
        </button>
        {conteggi.map(([x, n]) => (
          <button key={x} className={`dns-chip${tipo === x ? ' dns-chip--attivo' : ''}`} onClick={() => setTipo(tipo === x ? null : x)}>
            {x} <span className="dns-chip__n">{n}</span>
          </button>
        ))}
      </div>
      <div className="dns-filtri">
        <input type="text"
          value={filtroTesto}
          onChange={(e) => onFiltroTesto(e.target.value)}
          placeholder={t('Filtra per nome, valore o CI…')}
          aria-label={t('Filtra i record')}
          style={{ maxWidth: 320 }}
        />
        {microsoft && (
          <select value={registrazione} onChange={(e) => setRegistrazione(e.target.value as Registrazione)} aria-label={t('Registrazione')} style={{ width: 'auto' }}>
            <option value="tutti">{t('Statici e dinamici')}</option>
            <option value="dinamici">{t('Solo dinamici (registrati dai client)')}</option>
            <option value="statici">{t('Solo statici')}</option>
            <option value="vecchi">{t('Dinamici vecchi: oltre 30 giorni ({n})', { n: vecchi })}</option>
          </select>
        )}
        <label className="checkbox" style={{ margin: 0 }}>
          <input type="checkbox" checked={sistema} onChange={(e) => setSistema(e.target.checked)} />{' '}
          {t('Mostra SOA e NS della zona')}
        </label>
      </div>
      <DataTable
        rows={righe}
        initialSort={{ key: 'name' }}
        rowKey={(r, i) => `${r.name}-${r.type}-${r.content}-${i}`}
        columns={colonne}
        conteggio={{ nome: t('record') }}
        paginazione={{ predefinita: 50, opzioni: [25, 50, 100, 250, 1000] }}
        emptyText={t('Nessun record con questi filtri.')}
      />
    </>
  )
}

/** Search a name or a value across every zone already read (instant: on the copies). */
function RicercaGlobale({ server, onApri }: { server: string | null; onApri: (zona: string, filtro: string) => void }) {
  const [q, setQ] = useState('')
  const [esito, setEsito] = useState<DnsRicerca | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  useEffect(() => {
    const testo = q.trim()
    if (testo.length < 2) { setEsito(null); return }
    let vivo = true
    const id = setTimeout(() => {
      api.dnsSearch(testo, server)
        .then((r) => { if (vivo) { setEsito(r); setErrore(null) } })
        .catch((e) => { if (vivo) setErrore(e) })
    }, 300)
    return () => { vivo = false; clearTimeout(id) }
  }, [q, server])

  return (
    <div className="dns-ricerca">
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t('Cerca un nome o un indirizzo in tutte le zone…')}
        aria-label={t('Cerca in tutte le zone')}
      />
      <ErrorBanner error={errore} />
      {esito && (
        <div className="dns-ricerca__risultati">
          <p className="muted dns-ricerca__nota">
            {t('{n} risultati in {cercate} zone su {totali}', { n: esito.results.length, cercate: esito.zones_searched, totali: esito.zones_total })}
            {esito.zones_searched < esito.zones_total && ` · ${t('le altre non sono ancora state lette: lo fa il giro in sottofondo')}`}
            {esito.truncated && ` · ${t('mostrati i primi')}`}
          </p>
          {esito.results.length > 0 && (
            <ul className="dns-ricerca__elenco">
              {esito.results.map((r, i) => (
                <li key={`${r.zone_id}-${r.name}-${r.type}-${i}`}>
                  <button className="dns-ricerca__voce" onClick={() => { onApri(r.zone_id ?? '', r.name ?? ''); setQ('') }}>
                    <span className="mono">{r.name}</span>
                    <span className={`dns-tipo dns-tipo--${(r.type ?? 'x').toLowerCase()}`}>{r.type}</span>
                    <span className="mono muted">{r.content}</span>
                    <span className="muted dns-ricerca__zona">{r.zone_id}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
